import { Component, OnDestroy, OnInit } from '@angular/core';

import { Location } from '@angular/common'

import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { GlobalsService } from '../../services/globals.service';
import { MatDialog } from '@angular/material/dialog';
import { TransactionDetailModalComponent, GlTransactionDetailData } from '../../shared/transaction-detail-modal/transaction-detail-modal.component';
import { POSTED_FILTER_OPTIONS } from '../../constants/posted-filter-options';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
	selector: 'app-account-inquiry',
	templateUrl: './account-inquiry.component.html',
	styleUrls: ['./account-inquiry.component.scss']
})
export class AccountInquiryComponent implements OnInit, OnDestroy {
	periods: any = [];
	default_period: any = [];
	default_toperiod: any = '';
	default_fromperiod: any = '';
	formGroup: UntypedFormGroup;
	loading = false;
	trial_balance: any = [];
	title = '';
	from_period_options: any = [];
	to_period_options: any = [];
	tags: any = [];
	glaccounts: any = [];
	transresults: any = false;
	color: any = '';
	selectedaccount: any = false;
	periods_transactions: any = false;

	// ── Grid view state ────────────────────────────────────────────
	// Sort and filter are applied client-side to the rows already returned. The
	// inquiry is one query per Run; re-running the server for a column click
	// would also re-derive the running balance, which must stay in ledger order.
	filterText = '';
	sortKey = '';                        // '' = ledger order (period, trandate, counterindex)
	sortDir: 'asc' | 'desc' = 'asc';
	viewPeriods: any[] = [];             // period bands after filter + sort
	shownCount = 0;
	totalCount = 0;
	filteredNet = 0;
	spikeCount = 0;          // outlier TRANSACTIONS in the inquiry
	spikePeriodCount = 0;    // periods whose net is out of line
	spikesOnly = false;
	/** Collapsed period bands, keyed by periodno. Survives filter/sort rebuilds. */
	private collapsedBands = new Set<string>();
	allCollapsed = false;
	/** Set while a render big enough to be felt is in flight (expand-all on a large grid). */
	busy = false;
	private filterTimer: any;
	/** Rows above which an expand is worth telling the user about. */
	private readonly BUSY_ROW_THRESHOLD = 2000;
	/** A band nets more than this many times the median period in the range = spike. */
	private readonly SPIKE_MULTIPLE = 2;
	/** A row carrying this share of its period's gross movement is driving that period. */
	private readonly ROW_SPIKE_SHARE = 0.10;
	/** ...and it must also stand out from its neighbours, not just be one of five rows. */
	private readonly ROW_SPIKE_MEDIAN_MULTIPLE = 3;
	/** Below this many rows in a period, "share of the period" means nothing. */
	private readonly ROW_SPIKE_MIN_ROWS = 5;
	/** Row spikes are a property of the result set, not the view — computed once per inquiry. */
	private spikeSource: any = null;
	private spikeRowsByBand: number[] = [];

	postedOptions = POSTED_FILTER_OPTIONS;

	subed: any = false;
	isrunning: any = false;
	constructor(
		private location: Location,
		private generalLedgerService: GeneralLedgerService,
		private globalsService: GlobalsService,
		private globalSearchService: GlobalSearchService,
		private fb: UntypedFormBuilder,
		private dialog: MatDialog,
		private route: ActivatedRoute
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {

		this.formGroup = this.fb.group({
			glaccount: ['', [Validators.required]],
			basis: ['period', [Validators.required]],
			from_period: ['' , [Validators.required]],
			to_period: ['', [Validators.required]],
			from_date: [''],
			to_date: [''],
			gl_tag: [''],
			posted: ['all', [Validators.required]]
		});
		this.updateBasisValidators();

		this.generalLedgerService.getPeriods().subscribe((results: any) => {
			this.periods = results.periods;
			this.from_period_options = results.periods;
			this.to_period_options = results.periods;
			this.default_fromperiod = results.default_fromperiod;
			this.default_toperiod = results.default_toperiod;
			this.formGroup.get('from_period').setValue(results.default_fromperiod);
			this.formGroup.get('to_period').setValue(results.default_toperiod);
			this.setDefaultDateRange(results.default_fromperiod, results.default_toperiod);
			const qpm = this.route.snapshot.queryParamMap;
			const hasFromTo =
				(qpm.get('from_period') != null && qpm.get('from_period') !== '') ||
				(qpm.get('to_period') != null && qpm.get('to_period') !== '');
			const periodno = qpm.get('periodno');
			if (periodno && !hasFromTo) {
				this.formGroup.get('from_period').setValue(periodno);
				this.formGroup.get('to_period').setValue(periodno);
				this.setDefaultDateRange(periodno, periodno, true);
			}
			this.applyRoutePeriodQueryParams(qpm);
			const accountcode = qpm.get('accountcode');
			if (accountcode) {
				this.formGroup.get('glaccount').setValue(accountcode);
				this.globalsService.getGLAccount(accountcode).subscribe((acc: any) => {
					this.selectedaccount = { accountcode: acc.accountcode, accountname: acc.accountname };
					this.getAccount();
				});
			}
		});

		this.route.queryParams.subscribe(() => {
			if (!this.periods?.length || !this.formGroup) {
				return;
			}
			const qpm = this.route.snapshot.queryParamMap;
			const hasFromTo =
				(qpm.get('from_period') != null && qpm.get('from_period') !== '') ||
				(qpm.get('to_period') != null && qpm.get('to_period') !== '');
			const periodno = qpm.get('periodno');
			if (periodno && !hasFromTo && !qpm.get('accountcode')) {
				this.formGroup.get('from_period')?.setValue(periodno);
				this.formGroup.get('to_period')?.setValue(periodno);
				this.setDefaultDateRange(periodno, periodno, true);
			}
			this.applyRoutePeriodQueryParams(qpm);
		});
	}

	/** Deep-link: ?from_period=&to_period=&posted= (with or without accountcode). */
	private applyRoutePeriodQueryParams(qpm: ParamMap): void {
		if (!this.formGroup) {
			return;
		}
		const fp = qpm.get('from_period');
		const tp = qpm.get('to_period');
		const fd = qpm.get('from_date');
		const td = qpm.get('to_date');
		const pq = qpm.get('posted');
		if ((fd !== null && fd !== '') || (td !== null && td !== '')) {
			this.formGroup.get('basis')?.setValue('date', { emitEvent: false });
			if (fd !== null && fd !== '') {
				this.formGroup.get('from_date')?.setValue(this.parsePeriodDate(fd) || fd);
			}
			if (td !== null && td !== '') {
				this.formGroup.get('to_date')?.setValue(this.parsePeriodDate(td) || td);
			}
			this.updateBasisValidators();
		}
		if (fp !== null && fp !== '') {
			const fpN = parseInt(fp, 10);
			if (!isNaN(fpN)) {
				this.formGroup.get('from_period')?.setValue(fpN);
				this.updateToOptions(fpN);
			}
		}
		if (tp !== null && tp !== '') {
			const tpN = parseInt(tp, 10);
			if (!isNaN(tpN)) {
				this.formGroup.get('to_period')?.setValue(tpN);
			}
		}
		if (pq === 'all' || pq === 'posted' || pq === 'unposted') {
			this.formGroup.get('posted')?.setValue(pq);
		}
	}

	onBasisChange(): void {
		if (this.isDateMode()) {
			this.setDefaultDateRange(
				this.formGroup.get('from_period')?.value || this.default_fromperiod,
				this.formGroup.get('to_period')?.value || this.default_toperiod,
				true
			);
		} else {
			this.formGroup.patchValue({ from_date: '', to_date: '' }, { emitEvent: false });
		}
		this.updateBasisValidators();
		this.transresults = false;
		this.periods_transactions = false;
		this.rebuildView();
	}

	private updateBasisValidators(): void {
		if (!this.formGroup) {
			return;
		}
		const isDate = this.formGroup.get('basis')?.value === 'date';
		for (const key of ['from_period', 'to_period']) {
			const ctrl = this.formGroup.get(key);
			if (!ctrl) {continue;}
			if (isDate) {
				ctrl.clearValidators();
			} else {
				ctrl.setValidators([Validators.required]);
			}
			ctrl.updateValueAndValidity({ emitEvent: false });
		}
		for (const key of ['from_date', 'to_date']) {
			const ctrl = this.formGroup.get(key);
			if (!ctrl) {continue;}
			if (isDate) {
				ctrl.setValidators([Validators.required]);
			} else {
				ctrl.clearValidators();
			}
			ctrl.updateValueAndValidity({ emitEvent: false });
		}
	}

	isDateMode(): boolean {
		return this.formGroup?.get('basis')?.value === 'date';
	}

	private formatDateForApi(value: any): string {
		if (!value) {
			return '';
		}
		const d = value instanceof Date ? value : new Date(value);
		if (Number.isNaN(d.getTime())) {
			return String(value).slice(0, 10);
		}
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	getInquiryClosingLabel(): string {
		if (this.isDateMode()) {
			const to = this.formatDateForApi(this.formGroup?.get('to_date')?.value);
			return to || 'selected date';
		}
		return this.getPeriodEndDateLabel('to');
	}

	updateToOptions(value: any) {
		this.to_period_options = this.periods.filter((period) => {
			return parseInt(period.periodno) >= parseInt(value)
		});
		const to = this.formGroup?.get('to_period')?.value;
		if (!this.formGroup?.get('from_date')?.value || !this.formGroup?.get('to_date')?.value) {
			this.setDefaultDateRange(value, to || value);
		}
	}

	private parsePeriodDate(value: any): Date | null {
		const raw = String(value ?? '').trim();
		if (!raw) {return null;}
		const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
		if (iso) {return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));}
		const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
		if (us) {
			const year = Number(us[3].length === 2 ? `20${us[3]}` : us[3]);
			return new Date(year, Number(us[1]) - 1, Number(us[2]));
		}
		const d = new Date(raw);
		return Number.isNaN(d.getTime()) ? null : d;
	}

	/**
	 * Label for a period band. The API sends the period NUMBER in a field named
	 * `lastdate_in_period` (see model inquiry()), so the band used to read
	 * "Period ending 62". Resolve it against the loaded period list instead, and
	 * fall back to "Period n" if that lookup misses.
	 */
	periodLabel(transperiods: any): string {
		const raw = String(transperiods?.periodno ?? transperiods?.lastdate_in_period ?? '').trim();
		// Date-mode inquiries send a date RANGE here ("07/01/2026 – 07/31/2026"), and
		// parseInt() happily reads that as 7 — which then resolved against the period list
		// and labelled the band with whatever period 7 happens to be. Anything that is not
		// purely a period number is already a label; use it as one.
		if (raw !== '' && !/^\d+$/.test(raw)) {
			return raw;
		}
		const pn = parseInt(raw, 10);
		if (Number.isNaN(pn)) {return '';}

		// The period list already carries the label the From/To dropdowns show
		// ("January 2026"), so reuse it verbatim rather than round-tripping
		// through Date — the value is not always a parseable date string.
		const row = this.periods?.find((p: any) => parseInt(String(p.periodno), 10) === pn);
		if (row?.lastdate_in_period) {return String(row.lastdate_in_period);}

		const end = this.periodEndDate(pn);
		if (end) {return end.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });}
		return 'Period ' + pn;
	}

	private periodEndDate(periodno: any): Date | null {
		const pn = parseInt(String(periodno), 10);
		const row = this.periods?.find((p: any) => parseInt(String(p.periodno), 10) === pn);
		return row ? this.parsePeriodDate(row.lastdate_in_period) : null;
	}

	private periodStartDate(periodno: any): Date | null {
		const pn = parseInt(String(periodno), 10);
		const prev = this.periods?.find((p: any) => parseInt(String(p.periodno), 10) === pn - 1);
		const prevEnd = prev ? this.parsePeriodDate(prev.lastdate_in_period) : null;
		if (prevEnd) {
			prevEnd.setDate(prevEnd.getDate() + 1);
			return prevEnd;
		}
		return this.periodEndDate(periodno);
	}

	private setDefaultDateRange(fromPeriod: any, toPeriod: any, overwrite = false): void {
		const from = this.periodStartDate(fromPeriod);
		const to = this.periodEndDate(toPeriod);
		if (from && (overwrite || !this.formGroup.get('from_date')?.value)) {
			this.formGroup.get('from_date')?.setValue(from);
		}
		if (to && (overwrite || !this.formGroup.get('to_date')?.value)) {
			this.formGroup.get('to_date')?.setValue(to);
		}
	}

	/** End-of-period date label for From/To dropdowns (matches chartdetails period used for closing balance). */
	getPeriodEndDateLabel(which: 'from' | 'to'): string {
		const key = which === 'to' ? 'to_period' : 'from_period';
		const raw = this.formGroup?.get(key)?.value;
		if (raw === null || raw === undefined || raw === '' || !this.periods?.length) {
			return which === 'to' ? 'To period' : 'From period';
		}
		const pn = parseInt(String(raw), 10);
		if (isNaN(pn)) {
			return String(raw);
		}
		const row = this.periods.find((p: any) => parseInt(String(p.periodno), 10) === pn);
		return row?.lastdate_in_period ? String(row.lastdate_in_period) : `period ${pn}`;
	}

	inquirySpansMultiplePeriods(): boolean {
		const f = this.formGroup?.get('from_period')?.value;
		const t = this.formGroup?.get('to_period')?.value;
		if (f === null || f === undefined || f === '' || t === null || t === undefined || t === '') {
			return false;
		}
		return parseInt(String(f), 10) !== parseInt(String(t), 10);
	}

	ngOnDestroy(): void {
		clearTimeout(this.filterTimer);
		this.busy = false;
		if (this.subed) {
			this.subed.unsubscribe();
		}
	}

	back() {
		this.location.back();
	}



	getAccount() {
		if(this.formGroup.valid) {
			this.isrunning = true;
			if(this.subed) {
				this.subed.unsubscribe();
			}
			const raw = this.formGroup.value;
			const payload = this.isDateMode()
				? {
					glaccount: raw.glaccount,
					from_date: this.formatDateForApi(raw.from_date),
					to_date: this.formatDateForApi(raw.to_date),
					gl_tag: raw.gl_tag,
					posted: raw.posted
				}
				: {
					glaccount: raw.glaccount,
					from_period: raw.from_period,
					to_period: raw.to_period,
					gl_tag: raw.gl_tag,
					posted: raw.posted
				};
			this.subed = this.generalLedgerService.getAccountInquiry(payload).subscribe((results: any) => {
				this.isrunning = false;
				this.transresults = results;
				this.periods_transactions = results.periods;
				this.rebuildView();
			});
		}
	}

	selectGl(value: any) {
		this.selectedaccount = value.option.value;
		this.formGroup.get('glaccount').setValue(value.option.value.accountcode);
	}

	// ── Filter + sort ──────────────────────────────────────────────
	// Rebuilt imperatively rather than through a template-called getter: the grid
	// runs to thousands of rows on a control account, and a getter in *ngFor is
	// re-evaluated (and re-sorted) on every change-detection pass.

	onFilterChange(): void {
		// One rebuild per pause, not per keystroke: on a control account each pass filters and
		// re-sorts thousands of rows.
		clearTimeout(this.filterTimer);
		this.filterTimer = setTimeout(() => this.rebuildView(), 150);
	}

	clearFilter(): void {
		clearTimeout(this.filterTimer);
		this.filterText = '';
		this.rebuildView();
	}

	toggleSpikesOnly(): void {
		this.spikesOnly = !this.spikesOnly;
		this.rebuildView();
	}

	// ── Collapse ───────────────────────────────────────────────────
	// Collapsing hides a band's rows, leaving its figures. It is a view state, not a
	// filter: row counts, nets and the export still cover every row in the view.

	bandKey(band: any): string {
		return String(band?.periodno ?? band?.lastdate_in_period ?? '');
	}

	/**
	 * Collapse flips the flag ON THE BAND, never through rebuildView(). Rebuilding re-filters
	 * and re-sorts every row and hands *ngFor a fresh set of objects, so collapsing one period
	 * of a 3,000-row control account tore down and rebuilt the whole grid — tens of seconds for
	 * a control that hides one band. The set is kept in step so the state survives a real
	 * rebuild (filter, sort, re-run).
	 */
	toggleBand(band: any): void {
		const key = this.bandKey(band);
		if (this.collapsedBands.has(key)) {
			this.collapsedBands.delete(key);
			band.iscollapsed = false;
		} else {
			this.collapsedBands.add(key);
			band.iscollapsed = true;
		}
		this.allCollapsed = this.viewPeriods.length > 0 && this.viewPeriods.every((b: any) => b.iscollapsed);
	}

	toggleAllBands(): void {
		const expanding = this.allCollapsed;
		// Collapsing is cheap (nodes are only removed) and single bands are cheap either way.
		// Expanding every band of a big inquiry means Angular building thousands of rows, which
		// is the one case a user waits on — so flag it, let the flag paint, then do the work.
		if (expanding && this.shownCount > this.BUSY_ROW_THRESHOLD) {
			this.busy = true;
			// One macrotask so the flag paints before the work starts, then clear it a frame
			// after the rows exist — a plain setTimeout(0) fired before Angular had finished
			// building them, so the spinner vanished while the grid was still catching up.
			setTimeout(() => {
				this.applyAllBands(true);
				requestAnimationFrame(() => requestAnimationFrame(() => { this.busy = false; }));
			}, 0);
			return;
		}
		this.applyAllBands(expanding);
	}

	private applyAllBands(expanding: boolean): void {
		this.allCollapsed = !expanding;
		this.collapsedBands.clear();
		for (const band of this.viewPeriods) {
			band.iscollapsed = this.allCollapsed;
			if (this.allCollapsed) {
				this.collapsedBands.add(this.bandKey(band));
			}
		}
	}

	// *ngFor identity. Without these, every filter keystroke or sort click makes Angular
	// destroy and recreate every <tr> in the grid instead of moving the ones it already has.
	// Arrow properties, not methods: NgForOf calls trackBy unbound, so a method referring to
	// `this` throws inside change detection.
	trackBand = (index: number, band: any): string => this.bandKey(band);

	trackRow = (index: number, row: any): string => (
		row?.counterindex != null
			? String(row.counterindex)
			: String(row?.typeid ?? '') + '-' + String(row?.number ?? '') + '-' + index
	);

	sortBy(key: string): void {
		if (this.sortKey === key) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortKey = key;
			// Money and dates read newest/largest-first; text reads A→Z.
			this.sortDir = ['debit', 'credit', 'balance', 'date'].indexOf(key) > -1 ? 'desc' : 'asc';
		}
		this.rebuildView();
	}

	clearSort(): void {
		this.sortKey = '';
		this.sortDir = 'asc';
		this.rebuildView();
	}

	sortIcon(key: string): string {
		if (this.sortKey !== key) {
			return '';
		}
		return this.sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	ariaSort(key: string): string {
		if (this.sortKey !== key) {
			return 'none';
		}
		return this.sortDir === 'asc' ? 'ascending' : 'descending';
	}

	isFiltered(): boolean {
		return this.filterText.trim() !== '';
	}

	/** Any view showing less than the whole inquiry — the closing balance cannot tie to it. */
	private isSubsetView(): boolean {
		return this.isFiltered() || this.spikesOnly;
	}

	private rebuildView(): void {
		const src = Array.isArray(this.periods_transactions) ? this.periods_transactions : [];
		const terms = this.filterText.trim().toLowerCase().split(/\s+/).filter((t: string) => t !== '');
		// Trend figures are derived from EVERY band the inquiry returned, never from the
		// filtered subset: a running net that reset itself as you typed in the filter box
		// would be a different number every keystroke and tie to nothing in the ledger.
		// Row spikes belong to the result set, not the view, so they are computed once per
		// inquiry rather than on every keystroke — and the band trend reads their per-band
		// counts, so this runs first.
		if (this.spikeSource !== this.periods_transactions) {
			this.markRowSpikes(src);
			this.spikeSource = this.periods_transactions;
		}
		const trend = this.buildTrend(src);
		const bands: any[] = [];
		let shown = 0;
		let total = 0;
		let net = 0;
		// Counts are of the whole inquiry, not the view — the toggle has to keep its label
		// while it is filtering the view down to those same rows.
		this.spikePeriodCount = trend.filter((t: any) => t.isspike).length;
		if (!this.spikeCount) {
			this.spikesOnly = false;
		}

		for (let i = 0; i < src.length; i++) {
			const band = src[i];
			const rows = Array.isArray(band.transactions) ? band.transactions : [];
			total += rows.length;

			let kept = terms.length ? rows.filter((r: any) => this.rowMatches(r, terms)) : rows.slice();
			if (this.spikesOnly) {
				kept = kept.filter((r: any) => r.isspike);
			}
			// A band the filter emptied would otherwise render as a header for nothing.
			if ((terms.length || this.spikesOnly) && !kept.length) {
				continue;
			}

			let bandNet = 0;
			for (const r of kept) {
				bandNet += this.rowAmount(r);
			}
			shown += kept.length;
			net += bandNet;

			if (this.sortKey) {
				kept = this.sortRows(kept);
			}
			bands.push({
				...band,
				...trend[i],
				transactions: kept,
				filteredtotal: bandNet,
				filteredcount: kept.length,
				iscollapsed: this.collapsedBands.has(this.bandKey(band))
			});
		}

		this.viewPeriods = bands;
		this.allCollapsed = bands.length > 0 && bands.every((b: any) => b.iscollapsed);
		this.shownCount = shown;
		this.totalCount = total;
		this.filteredNet = net;
	}

	/**
	 * Flags the transactions DRIVING each period, which is the question behind "why is this
	 * month off": a row is flagged when it carries at least a tenth of everything that moved
	 * in its period AND is several times the period's median row.
	 *
	 * Statistical outlier tests were tried first and both failed on real ledger data. On Cleve
	 * 7051-0 period 64 (3,701 rows, median $171) an Iglewicz–Hoaglin modified z-score over the
	 * raw amounts flagged 306 rows — 8% of the period, which is a long tail, not a spike list.
	 * The same test over log amounts flagged none, putting the cutoff at $175k. GL amounts are
	 * heavy-tailed, so neither says anything useful; share-of-period does, and it is a figure
	 * an accountant can check by hand.
	 *
	 * A period whose net is out of line but where nothing crosses this bar is a real answer,
	 * not a gap: the swing came from volume, and the band says so.
	 *
	 * Flags are written onto the rows themselves: cloning 12k rows per rebuild to carry three
	 * fields is what made this grid slow in the first place.
	 */
	private markRowSpikes(src: any[]): void {
		this.spikeCount = 0;
		this.spikeRowsByBand = [];

		for (let i = 0; i < src.length; i++) {
			const rows = Array.isArray(src[i].transactions) ? src[i].transactions : [];
			let bandSpikes = 0;

			for (const row of rows) {
				row.isspike = false;
				row.spikeshare = 0;
				row.spikereason = '';
			}

			if (rows.length >= this.ROW_SPIKE_MIN_ROWS) {
				const magnitudes = rows.map((r: any) => Math.abs(this.rowAmount(r)));
				const gross = magnitudes.reduce((a: number, b: number) => a + b, 0);
				const median = this.medianOf(magnitudes.filter((n: number) => n > 0));
				const shareFloor = gross * this.ROW_SPIKE_SHARE;
				const medianFloor = median * this.ROW_SPIKE_MEDIAN_MULTIPLE;

				if (gross > 0 && median > 0) {
					const label = this.periodLabel(src[i]) || 'this period';
					for (let r = 0; r < rows.length; r++) {
						const magnitude = magnitudes[r];
						if (magnitude < shareFloor || magnitude < medianFloor) {
							continue;
						}
						const share = Math.round((magnitude / gross) * 1000) / 10;
						rows[r].isspike = true;
						rows[r].spikeshare = share;
						rows[r].spikereason = share + '% of ' + label + ' movement · '
							+ (Math.round((magnitude / median) * 10) / 10) + '\u00d7 the median row';
						bandSpikes++;
						this.spikeCount++;
					}
				}
			}
			this.spikeRowsByBand.push(bandSpikes);
		}
	}

	private medianOf(values: number[]): number {
		if (!values.length) {
			return 0;
		}
		const sorted = values.slice().sort((a: number, b: number) => a - b);
		const mid = Math.floor(sorted.length / 2);
		return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
	}

	/**
	 * Per-band trend figures: the range's cumulative net through each band, the change against
	 * the prior period, and a spike flag.
	 *
	 * Running net is the movement of the RANGE (it starts at zero on the first band), not the
	 * account balance — the balance column already carries that. Read together they answer the
	 * two different questions: "where is this account" and "what has this range done so far".
	 *
	 * A spike is a band whose net is far out of line with the rest of the range, measured
	 * against the MEDIAN absolute net rather than the mean, so one outlier does not raise the
	 * bar enough to hide itself. Needs at least three bands to mean anything.
	 */
	private buildTrend(src: any[]): any[] {
		const nets = src.map((b: any) => this.toNumber(b.periodtotal));
		const median = this.medianOf(nets.map((n: number) => Math.abs(n)).filter((n: number) => n > 0));
		const canFlag = src.length >= 3 && median > 0;
		const threshold = median * this.SPIKE_MULTIPLE;

		// A single band's running net would just restate its period net — hide it there.
		const showRunning = src.length > 1;
		let running = 0;
		return src.map((band: any, i: number) => {
			const bandNet = nets[i];
			running += bandNet;
			const hasPrior = this.hasPriorPeriodNet(band);
			const priorNet = hasPrior ? this.toNumber(band.priorperiodtotal) : null;
			const multiple = median > 0 ? Math.abs(bandNet) / median : 0;
			const isspike = canFlag && Math.abs(bandNet) > threshold;
			const bandSpikeRows = this.spikeRowsByBand[i] || 0;
			return {
				spikerowcount: bandSpikeRows,
				runningnet: showRunning ? Math.round(running * 100) / 100 : null,
				deltaprior: hasPrior ? Math.round((bandNet - (priorNet as number)) * 100) / 100 : null,
				isspike,
				spikemultiple: Math.round(multiple * 10) / 10,
				spikereason: isspike
					? (Math.round(multiple * 10) / 10) + '\u00d7 median period net'
						+ (bandSpikeRows ? '' : ' — no single transaction dominates, this is volume')
					: ''
			};
		});
	}

	/** Signed movement of one row — debit positive, credit negative (matches gltrans.amount). */
	private rowAmount(row: any): number {
		const debit = parseFloat(String(row?.debit ?? '').replace(/[^0-9.\-]/g, ''));
		const credit = parseFloat(String(row?.credit ?? '').replace(/[^0-9.\-]/g, ''));
		return (Number.isNaN(debit) ? 0 : debit) - (Number.isNaN(credit) ? 0 : credit);
	}

	/** Every term must appear somewhere in the row, so "chk 1042" narrows instead of widening. */
	private rowMatches(row: any, terms: string[]): boolean {
		const hay = [
			row?.type, row?.number, row?.trandate, row?.debit, row?.credit,
			row?.chequeno, row?.check, row?.balance, row?.debtorno, row?.debtorname,
			row?.narrative, row?.tag
		].map((v: any) => (v === null || v === undefined) ? '' : String(v)).join(' ').toLowerCase();

		for (const t of terms) {
			if (hay.indexOf(t) === -1) {
				return false;
			}
		}
		return true;
	}

	private sortRows(rows: any[]): any[] {
		const dir = this.sortDir === 'asc' ? 1 : -1;
		const key = this.sortKey;
		// Bands keep their own order; sorting is within a period, never across one,
		// because each band carries its own opening balance and net.
		return rows.slice().sort((a: any, b: any) => {
			const av = this.sortValue(a, key);
			const bv = this.sortValue(b, key);
			if (typeof av === 'number' && typeof bv === 'number') {
				return (av - bv) * dir;
			}
			return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
		});
	}

	private sortValue(row: any, key: string): string | number {
		switch (key) {
		case 'type':
			return String(row?.type ?? '');
		case 'number':
			return this.toNumber(row?.number);
		case 'date': {
			const d = this.parsePeriodDate(row?.trandate);
			return d ? d.getTime() : 0;
		}
		case 'debit':
			return this.toNumber(row?.debit);
		case 'credit':
			return this.toNumber(row?.credit);
		case 'balance':
			return this.toNumber(row?.balance);
		case 'check':
			return String(row?.chequeno ?? row?.check ?? '');
		case 'customer':
			return (String(row?.debtorno ?? '') + ' ' + String(row?.debtorname ?? '')).trim();
		case 'desc':
			return String(row?.narrative ?? '');
		case 'tag':
			return String(row?.tag ?? '');
		default:
			return '';
		}
	}

	private toNumber(value: any): number {
		const n = parseFloat(String(value ?? '').replace(/[^0-9.\-]/g, ''));
		return Number.isNaN(n) ? 0 : n;
	}

	/** Prior-period net comes from the API; a band predating the ledger simply has none. */
	hasPriorPeriodNet(band: any): boolean {
		return band?.priorperiodtotal !== null && band?.priorperiodtotal !== undefined && band?.priorperiodtotal !== '';
	}

	/** Synthetic “prior unposted” bridge row from inquiry API when Posted = All and range misses older unposted. */
	hasUnpostedBridgeRow(): boolean {
		if (!this.periods_transactions?.length) {
			return false;
		}
		for (const p of this.periods_transactions) {
			for (const t of p.transactions || []) {
				if (t.adjustment_only === 1 || t.adjustment_only === true) {
					return true;
				}
			}
		}
		return false;
	}

	openTransactionModal(trans: any, transperiods: any) {
		if (trans.adjustment_only === 1 || trans.adjustment_only === true) {
			return;
		}
		const data: GlTransactionDetailData = {
			type: trans.type,
			typeid: trans.typeid,
			number: trans.number,
			trandate: trans.trandate,
			debit: trans.debit,
			credit: trans.credit,
			chequeno: trans.chequeno || trans.check,
			narrative: trans.narrative,
			balance: trans.balance,
			tag: trans.tag,
			periodno: transperiods?.periodno,
			periodname: transperiods?.lastdate_in_period,
			counterindex: trans.counterindex,
			tagref: trans.tagref != null ? trans.tagref : null,
			debtorno: trans.debtorno != null ? trans.debtorno : null,
			debtorname: trans.debtorname != null ? trans.debtorname : null
		};

		this.dialog.open(TransactionDetailModalComponent, {
			width: '1100px',
			maxWidth: '94vw',
			data
		}).afterClosed().subscribe((result: { saved?: boolean } | undefined) => {
			if (result?.saved) {
				this.getAccount();
			}
		});
	}

	/** Rows currently on screen — the Export buttons have nothing to write without them. */
	get hasExportableRows(): boolean {
		return this.shownCount > 0;
	}

	private exportName(ext: string): string {
		const code = this.selectedaccount?.accountcode
			? String(this.selectedaccount.accountcode).replace(/[^A-Za-z0-9_-]+/g, '')
			: 'all';
		return 'AccountInquiry_' + code + '_' + this.formatDateForApi(new Date()) + '.' + ext;
	}

	private exportTitle(): string {
		const code = this.selectedaccount?.accountcode ? String(this.selectedaccount.accountcode) : '';
		const name = this.selectedaccount?.accountname ? String(this.selectedaccount.accountname) : '';
		return (code + ' ' + name).trim() || 'Account Inquiry';
	}

	/** Label/value pairs for the header block of both exports. Values stay typed for XLSX. */
	private exportSummary(): Array<[string, any, boolean]> {
		const pairs: Array<[string, any, boolean]> = [];
		pairs.push([this.isDateMode() ? 'Dates' : 'Periods',
			this.isDateMode()
				? this.formatDateForApi(this.formGroup?.get('from_date')?.value) + '  to  ' + this.formatDateForApi(this.formGroup?.get('to_date')?.value)
				: this.getPeriodEndDateLabel('from') + '  to  ' + this.getPeriodEndDateLabel('to'),
			false]);
		pairs.push(['Posted', this.postedLabel(), false]);
		const tag = this.formGroup?.get('gl_tag')?.value;
		if (tag) {
			pairs.push(['GL tag', tag, false]);
		}
		if (this.transresults?.openbalance !== undefined && this.transresults?.openbalance !== null) {
			pairs.push(['Opening balance', this.toNumber(this.transresults.openbalance), true]);
		}
		pairs.push(['Closing balance', this.toNumber(this.transresults?.endbalance), true]);
		pairs.push(['Rows', this.shownCount === this.totalCount
			? String(this.totalCount)
			: this.shownCount + ' of ' + this.totalCount, false]);
		if (this.isFiltered()) {
			pairs.push(['Filter', '"' + this.filterText.trim() + '"', false]);
		}
		if (this.spikesOnly) {
			pairs.push(['View', 'Spike transactions only', false]);
		}
		if (this.sortKey) {
			pairs.push(['Sort', this.sortLabel(this.sortKey) + ' ' + this.sortDir, false]);
		}
		if (this.spikeCount || this.spikePeriodCount) {
			pairs.push(['Spikes', this.spikeCount + ' transaction' + (this.spikeCount === 1 ? '' : 's')
				+ ' · ' + this.spikePeriodCount + ' period' + (this.spikePeriodCount === 1 ? '' : 's'), false]);
		}
		pairs.push(['Exported', new Date().toLocaleString(), false]);
		return pairs;
	}

	private static readonly EXPORT_COLS = [
		'Period', 'Type', 'Number', 'Date', 'Debit', 'Credit', 'Check #', 'Balance',
		'Customer #', 'Customer', 'Description', 'Tag',
		'Period opening', 'Period prior net', 'Period net', 'Delta vs prior', 'Running net',
		'Row spike %', 'Period spike'
	];

	/** Excel serial for a m/d/Y string — a real date cell sorts chronologically; text does not. */
	private excelSerial(value: any): number | null {
		const d = this.parsePeriodDate(value);
		if (!d) {
			return null;
		}
		const utc = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
		return (utc - Date.UTC(1899, 11, 30)) / 86400000;
	}

	/** Document/check numbers: numeric when they are numbers, so they sort as numbers. */
	private numericOrText(value: any): any {
		const raw = String(value ?? '').trim();
		if (raw === '') {
			return '';
		}
		return /^\d+$/.test(raw) ? Number(raw) : raw;
	}

	private exportRows(): any[][] {
		const out: any[][] = [];
		for (const band of this.viewPeriods) {
			const label = this.periodLabel(band);
			let first = true;
			for (const t of band.transactions || []) {
				const debit = this.toNumber(t.debit);
				const credit = this.toNumber(t.credit);
				out.push([
					label,
					t.type ?? '',
					this.numericOrText(t.number),
					this.excelSerial(t.trandate),
					debit ? debit : '',
					credit ? credit : '',
					this.numericOrText(t.chequeno || t.check),
					this.toNumber(t.balance),
					t.debtorno ?? '',
					t.debtorname ?? '',
					t.narrative ?? '',
					t.tag ?? '',
					first ? this.toNumber(band.startbalance) : '',
					(first && this.hasPriorPeriodNet(band)) ? this.toNumber(band.priorperiodtotal) : '',
					first ? this.toNumber(band.periodtotal) : '',
					(first && band.deltaprior !== null && band.deltaprior !== undefined) ? band.deltaprior : '',
					(first && band.runningnet !== null && band.runningnet !== undefined) ? band.runningnet : '',
					t.isspike ? t.spikeshare : '',
					(first && band.isspike) ? band.spikemultiple : ''
				]);
				first = false;
			}
		}
		return out;
	}

	/**
	 * Styled XLSX, same idiom as the remittance/statement exports: title band, summary block,
	 * header row, banded rows, autofilter, and — the point of the rewrite — REAL typed cells.
	 * Amounts are numeric with a money format and dates are Excel serials with a date format,
	 * so both actually sort. The previous version wrote every figure as text, which sorts
	 * lexically ("$9.00" above "$10.00") and cannot be summed in the sheet.
	 */
	exportExcel(): void {
		const data = this.exportRows();
		if (!data.length) {
			return;
		}
		const cols = AccountInquiryComponent.EXPORT_COLS;
		const NC = cols.length;
		const money = '"$"#,##0.00;("$"#,##0.00)';
		const dateFmt = 'mm/dd/yyyy';
		const pctFmt = '0.0"%"';
		const multFmt = '0.0"\u00d7"';
		const HEAD = '183D78', WHITE = 'FFFFFF', BAND = 'EEF3FB', BORD = 'DADDE3', INK = '1F2430', MUT = '6B7280';
		const SPIKE = 'FFF4E0';
		const thin = { style: 'thin', color: { rgb: BORD } };
		const box = { top: thin, bottom: thin, left: thin, right: thin };
		const moneyCols = [4, 5, 7, 12, 13, 14, 15, 16];

		const rows: any[][] = [];
		rows.push(['Account Inquiry']);
		rows.push([this.exportTitle()]);
		rows.push([]);
		const summary = this.exportSummary();
		const sum0 = rows.length;
		for (const pair of summary) {
			rows.push([pair[0], pair[1]]);
		}
		const sum1 = rows.length - 1;
		rows.push([]);
		const hRow = rows.length;
		rows.push(cols.slice());
		const d0 = rows.length;
		let debits = 0;
		let credits = 0;
		for (const row of data) {
			debits += this.toNumber(row[4]);
			credits += this.toNumber(row[5]);
			rows.push(row);
		}
		const d1 = rows.length - 1;
		const totalLabel = (this.isSubsetView() ? 'FILTERED TOTAL — ' : 'TOTAL — ')
			+ data.length + (data.length === 1 ? ' row' : ' rows');
		const totals: any[] = new Array(NC).fill('');
		totals[0] = totalLabel;
		totals[4] = Math.round(debits * 100) / 100;
		totals[5] = Math.round(credits * 100) / 100;
		// The closing balance belongs to the whole inquiry, so it would misread against a subset.
		totals[7] = this.isSubsetView() ? '' : this.toNumber(this.transresults?.endbalance);
		rows.push(totals);
		const tRow = rows.length - 1;

		const ws: any = XLSX.utils.aoa_to_sheet(rows);
		ws['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 11 }, { wch: 13 }, { wch: 13 }, { wch: 10 },
			{ wch: 14 }, { wch: 11 }, { wch: 24 }, { wch: 46 }, { wch: 12 },
			{ wch: 15 }, { wch: 15 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 11 }, { wch: 12 }];
		ws['!rows'] = [];
		ws['!rows'][0] = { hpt: 26 };
		ws['!rows'][1] = { hpt: 16 };
		ws['!merges'] = [
			{ s: { r: 0, c: 0 }, e: { r: 0, c: NC - 1 } },
			{ s: { r: 1, c: 0 }, e: { r: 1, c: NC - 1 } }
		];
		// Data only — a total row inside the filter range gets dragged into every sort.
		ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: hRow, c: 0 }, e: { r: d1, c: NC - 1 } }) };

		const cell = (ri: number, ci: number): any => {
			const a = XLSX.utils.encode_cell({ r: ri, c: ci });
			if (!ws[a]) {
				ws[a] = { t: 's', v: '' };
			}
			return ws[a];
		};

		cell(0, 0).s = { font: { bold: true, sz: 16, color: { rgb: WHITE } }, fill: { fgColor: { rgb: HEAD } },
			alignment: { vertical: 'center', horizontal: 'left', indent: 1 } };
		cell(1, 0).s = { font: { sz: 11, color: { rgb: MUT } }, alignment: { horizontal: 'left', indent: 1 } };

		for (let i = 0; i < summary.length; i++) {
			const ri = sum0 + i;
			cell(ri, 0).s = { font: { bold: true, color: { rgb: INK } } };
			const v = cell(ri, 1);
			if (summary[i][2]) {
				v.t = 'n';
				v.z = money;
				v.s = { font: { color: { rgb: INK } }, numFmt: money, alignment: { horizontal: 'left' } };
			} else {
				v.s = { font: { color: { rgb: INK } } };
			}
		}

		for (let ci = 0; ci < NC; ci++) {
			cell(hRow, ci).s = { font: { bold: true, color: { rgb: WHITE } }, fill: { fgColor: { rgb: HEAD } },
				alignment: { vertical: 'center', horizontal: moneyCols.indexOf(ci) > -1 ? 'right' : 'left', wrapText: true },
				border: box };
		}

		for (let ri = d0; ri <= d1; ri++) {
			const isSpikeRow = rows[ri][17] !== '' && rows[ri][17] !== null;
			const band = ((ri - d0) % 2) === 1;
			for (let ci = 0; ci < NC; ci++) {
				const cc = cell(ri, ci);
				const st: any = { border: box, alignment: { vertical: 'center' } };
				if (isSpikeRow) {
					st.fill = { fgColor: { rgb: SPIKE } };
				} else if (band) {
					st.fill = { fgColor: { rgb: BAND } };
				}
				if (moneyCols.indexOf(ci) > -1) {
					if (cc.v !== '' && cc.v !== null) {
						cc.t = 'n';
						cc.z = money;
						st.numFmt = money;
					}
					st.alignment.horizontal = 'right';
				} else if (ci === 3) {
					if (cc.v !== '' && cc.v !== null) {
						cc.t = 'n';
						cc.z = dateFmt;
						st.numFmt = dateFmt;
					}
					st.alignment.horizontal = 'right';
				} else if (ci === 2 || ci === 6) {
					st.alignment.horizontal = 'right';
				} else if (ci === 17 || ci === 18) {
					if (cc.v !== '' && cc.v !== null) {
						cc.t = 'n';
						cc.z = ci === 17 ? pctFmt : multFmt;
						st.numFmt = ci === 17 ? pctFmt : multFmt;
					}
					st.alignment.horizontal = 'right';
					st.font = { bold: true, color: { rgb: '7A4B00' } };
				}
				cc.s = st;
			}
		}

		for (let ci = 0; ci < NC; ci++) {
			const cc = cell(tRow, ci);
			const st: any = { font: { bold: true, color: { rgb: INK } }, fill: { fgColor: { rgb: 'DCE6F5' } },
				border: { top: { style: 'medium', color: { rgb: HEAD } }, bottom: thin, left: thin, right: thin },
				alignment: { vertical: 'center' } };
			if (moneyCols.indexOf(ci) > -1) {
				if (cc.v !== '' && cc.v !== null) {
					cc.t = 'n';
					cc.z = money;
					st.numFmt = money;
				}
				st.alignment.horizontal = 'right';
			}
			cc.s = st;
		}

		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Account Inquiry');
		XLSX.writeFile(wb, this.exportName('xlsx'));
	}

	/**
	 * PDF via jsPDF + autoTable, matching the remittance/statement exports. Replaces the old
	 * route that base64'd the grid's innerHTML through the print service, which reproduced the
	 * screen's HTML (and its column widths) rather than laying out a report.
	 */
	exportPdf(): void {
		const data = this.exportRows();
		if (!data.length) {
			return;
		}
		const doc = new jsPDF({ orientation: 'landscape' });
		doc.setFontSize(14);
		doc.text('Account Inquiry — ' + this.exportTitle(), 14, 14);
		doc.setFontSize(8);
		let y = 20;
		for (const pair of this.exportSummary()) {
			const value = pair[2] ? this.moneyText(pair[1]) : String(pair[1]);
			doc.text(pair[0] + ':  ' + value, 14, y);
			y += 4;
		}

		// Period bands become full-width group rows, so the PDF reads like the screen: each
		// month's figures above its transactions instead of repeating down a column.
		const NC = 10;
		const body: any[] = [];
		const spikeRowIndexes: number[] = [];
		for (const band of this.viewPeriods) {
			const stats = ['Open ' + this.moneyText(band.startbalance),
				this.hasPriorPeriodNet(band) ? 'Prior ' + this.moneyText(band.priorperiodtotal) : '',
				'Net ' + this.moneyText(band.periodtotal),
				// 'Δ' is not in jsPDF's WinAnsi encoding — it is silently dropped, leaving a
				// figure with no label. Spell it out for the PDF.
				(band.deltaprior !== null && band.deltaprior !== undefined) ? 'Chg vs prior ' + this.moneyText(band.deltaprior) : '',
				(band.runningnet !== null && band.runningnet !== undefined) ? 'Running ' + this.moneyText(band.runningnet) : '',
				band.isspike ? 'SPIKE ' + band.spikemultiple + 'x median' : ''
			].filter((p: string) => p !== '').join('   ·   ');
			body.push([{ content: this.periodLabel(band) + '   ·   ' + stats, colSpan: NC,
				styles: { fontStyle: 'bold', fillColor: band.isspike ? [255, 244, 224] : [238, 241, 245], textColor: [33, 37, 41] } }]);
			for (const t of band.transactions || []) {
				if (t.isspike) {
					spikeRowIndexes.push(body.length);
				}
				body.push([
					t.type ?? '', t.number ?? '', t.trandate ?? '',
					+t.debit ? this.moneyText(t.debit) : '',
					+t.credit ? this.moneyText(t.credit) : '',
					t.chequeno || t.check || '',
					this.moneyText(t.balance),
					((t.debtorno ? t.debtorno + ' ' : '') + (t.debtorname || '')).trim(),
					t.narrative ?? '',
					(t.isspike ? t.spikeshare + '%' : '') + (t.tag ? (t.isspike ? ' · ' : '') + t.tag : '')
				]);
			}
		}

		autoTable(doc, {
			startY: y + 2,
			head: [['Type', 'Number', 'Date', 'Debit', 'Credit', 'Check #', 'Balance', 'Customer', 'Description', 'Tag']],
			body,
			styles: { fontSize: 7, cellPadding: 1.5, overflow: 'ellipsize' },
			headStyles: { fillColor: [24, 61, 120] },
			columnStyles: {
				3: { halign: 'right' }, 4: { halign: 'right' }, 6: { halign: 'right' },
				8: { cellWidth: 70 }
			},
			didParseCell: (hook: any) => {
				if (hook.section === 'body' && spikeRowIndexes.indexOf(hook.row.index) > -1) {
					hook.cell.styles.fillColor = [255, 248, 236];
					if (hook.column.index === 3 || hook.column.index === 4) {
						hook.cell.styles.fontStyle = 'bold';
					}
				}
			}
		});
		doc.save(this.exportName('pdf'));
	}

	/** Plain money text for the PDF — no currency pipe available outside the template. */
	private moneyText(value: any): string {
		const n = this.toNumber(value);
		const text = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		return (n < 0 ? '-$' : '$') + text;
	}

	private postedLabel(): string {
		const value = this.formGroup?.get('posted')?.value;
		const match = this.postedOptions.find((o: any) => o.value === value);
		return match ? match.label : String(value ?? '');
	}

	private sortLabel(key: string): string {
		const labels: any = {
			type: 'Type', number: 'Number', date: 'Date', debit: 'Debit', credit: 'Credit',
			balance: 'Balance', check: 'Check #', customer: 'Customer', desc: 'Description', tag: 'Tag'
		};
		return labels[key] || key;
	}
}
